import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface PaymentDetails {
  id: string;
  amount: number;
  currency: string;
  currencySymbol: string;
  subscriptionPlan: string;
  subscriptionDuration: number;
  paymentStatus: string;
  school: {
    name: string;
    code: string;
    email: string;
  };
}

const PaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  const [payment, setPayment] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    paymentMethod: 'bank_transfer',
    paymentReference: '',
    notes: ''
  });

  useEffect(() => {
    if (token) {
      fetchPaymentDetails();
    }
  }, [token]);

  const fetchPaymentDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/verify-token/${token}`);
      setPayment(response.data.payment);
      
      if (response.data.payment.paymentStatus !== 'pending') {
        setError(`This payment is already ${response.data.payment.paymentStatus}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid payment token');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API_URL}/payments/submit-proof`, {
        paymentToken: token,
        ...formData
      });
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanColor = (plan: string) => {
    const colors: any = {
      trial: 'text-blue-600',
      basic: 'text-purple-600',
      standard: 'text-indigo-600',
      premium: 'text-pink-600'
    };
    return colors[plan] || colors.trial;
  };

  const getPlanName = (plan: string) => {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className={`text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Loading payment details...</div>
      </div>
    );
  }

  if (error && !payment) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className={`max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-8 text-center`}>
          <div className="text-6xl mb-4">❌</div>
          <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Invalid Token</h2>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className={`max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-8 text-center`}>
          <div className="text-6xl mb-4">✅</div>
          <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Payment Submitted!</h2>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
            Your payment proof has been submitted successfully. Our team will verify and activate your subscription within 24 hours.
          </p>
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 mb-6`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>You'll receive a confirmation email at:</p>
            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{payment?.school.email}</p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} py-12 px-4`}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            💳 Complete Your Payment
          </h1>
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-md ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
        </div>

        {/* Payment Details Card */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-8 mb-6`}>
          <h2 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Subscription Details
          </h2>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>School Name</span>
              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {payment?.school.name}
              </span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>School Code</span>
              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {payment?.school.code}
              </span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Subscription Plan</span>
              <span className={`font-bold text-lg ${getPlanColor(payment?.subscriptionPlan || '')}`}>
                {getPlanName(payment?.subscriptionPlan || '')}
              </span>
            </div>
            
            <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Duration</span>
              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {payment?.subscriptionDuration} months
              </span>
            </div>
            
            <div className="flex justify-between items-center pt-4">
              <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Total Amount</span>
              <span className="text-3xl font-bold text-purple-600">
                {payment?.currencySymbol}{payment?.amount}
              </span>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className={`${isDarkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'} border rounded-lg p-4 mb-6`}>
            <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-900'}`}>
              📌 Payment Instructions
            </h3>
            <ol className={`list-decimal list-inside space-y-1 text-sm ${isDarkMode ? 'text-purple-200' : 'text-purple-800'}`}>
              <li>Make payment using your preferred method</li>
              <li>Keep your payment reference/transaction ID</li>
              <li>Fill in the payment details below</li>
              <li>Submit for verification</li>
              <li>Your account will be activated within 24 hours</li>
            </ol>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Payment Method *
              </label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                required
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Credit/Debit Card</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="paypal">PayPal</option>
                <option value="crypto">Cryptocurrency</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Payment Reference / Transaction ID *
              </label>
              <input
                type="text"
                value={formData.paymentReference}
                onChange={(e) => setFormData({ ...formData, paymentReference: e.target.value })}
                placeholder="Enter your transaction reference"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'
                }`}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Additional Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information about your payment..."
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'
                }`}
              />
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className={`flex-1 px-6 py-3 border rounded-lg font-semibold ${
                  isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || payment?.paymentStatus !== 'pending'}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          </form>
        </div>

        {/* Help Section */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 text-center`}>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            Need help? Contact us at{' '}
            <a href={`mailto:${payment?.school.email}`} className="text-purple-600 hover:text-purple-700 font-semibold">
              {payment?.school.email}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
